/**
 * useJarvisRouteContext
 *
 * Frontend-Hook der auf Route-Wechsel reagiert und den aktuellen
 * Seitenkontext (welcher Kunde/Anfrage/Projekt/Termin gerade offen) an
 * das Backend pusht. Kiki kann den Wert dann via `aktueller_kontext`-Tool
 * lesen, damit der Nutzer Sachen sagen kann wie „erstell Rechnung über
 * 250 Euro" ohne den Kundennamen wiederholen zu müssen.
 *
 * Wann pushen:
 *   - Bei jeder Route-Änderung — aber NUR wenn der User in eine HeyKiki-
 *     Org eingeloggt ist (Standalone-Orgs haben gar keinen Jarvis).
 *   - Debounced minimal (50ms) damit React-Strict-Mode-Doppel-Effects
 *     nicht doppelt feuern.
 *
 * Was wird gepusht:
 *   { kundeId?, anfrageId?, projektId?, terminId?, pageType, pageLabel }
 *
 * Route-Extraktion:
 *   - /kunden/:id       → { kundeId: ':id', pageType: 'kunde' }
 *   - /anfragen/:id     → { anfrageId: ':id', pageType: 'anfrage' }
 *   - /projekte/:id     → { projektId: ':id', pageType: 'projekt' }
 *   - /projekte/:id/bearbeiten → wie /projekte/:id
 *   - alle anderen Routen → { pageType: 'other' } (Cache wird neutral)
 *
 * Auth: nutzt den gleichen Bearer-Token aus localStorage wie alle anderen
 * Frontend-Requests.
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

// Pfad-Regex → Context-Resolver. Reihenfolge wichtig (spezifischere zuerst).
const ROUTE_PATTERNS = [
  // /kunden/<uuid> — auch trailing slashes/segmente erlauben (z.B. /kunden/abc/notizen)
  {
    re: /^\/kunden\/([a-f0-9-]{6,})(?:\/|$)/i,
    build: (m) => ({ kundeId: m[1], pageType: 'kunde' }),
  },
  {
    re: /^\/anfragen\/([a-f0-9-]{6,})(?:\/|$)/i,
    build: (m) => ({ anfrageId: m[1], pageType: 'anfrage' }),
  },
  {
    re: /^\/projekte\/([a-f0-9-]{6,})(?:\/|$)/i,
    build: (m) => ({ projektId: m[1], pageType: 'projekt' }),
  },
  {
    re: /^\/termine\/([a-f0-9-]{6,})(?:\/|$)/i,
    build: (m) => ({ terminId: m[1], pageType: 'termin' }),
  },
];

function extractContextFromPath(pathname) {
  for (const p of ROUTE_PATTERNS) {
    const m = pathname.match(p.re);
    if (m) return p.build(m);
  }
  return { pageType: 'other' };
}

export default function useJarvisRouteContext({ enabled = true } = {}) {
  const location = useLocation();
  const lastPushedRef = useRef(null);

  useEffect(() => {
    if (!enabled) return;
    const token = localStorage.getItem('token');
    if (!token) return;

    const ctx = extractContextFromPath(location.pathname);
    // Dedup: gleicher pathname → kein Re-Push (vermeidet Doppelung bei
    // Strict-Mode / Re-Renders).
    if (lastPushedRef.current === location.pathname) return;
    lastPushedRef.current = location.pathname;

    // Mini-Debounce damit beim Mount nicht direkt zwei Calls feuern.
    const timer = setTimeout(() => {
      axios
        .post(`${API_URL}/heykiki/jarvis/context`, ctx, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .catch(() => {
          // Fehlschlag still — Kiki funktioniert auch ohne Kontext-Cache
          // (der Nutzer muss dann halt den Namen nennen).
        });
    }, 60);

    return () => clearTimeout(timer);
  }, [location.pathname, enabled]);
}
